<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import screenshot1 from './feature-screenshot-1.png';
import screenshot2 from './feature-screenshot-2.png';
import screenshot3 from './feature-screenshot-3.png';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const current = ref(0);
let timer = null;

const slides = computed(() => [
  {
    tag: t('HOME.SPOTLIGHT.TAG_NEW'),
    version: 'v2.4',
    color: 'linear-gradient(135deg,#7c6cf0,#6d5ce7)',
    title: t('HOME.SPOTLIGHT.SLIDE_1_TITLE'),
    body: t('HOME.SPOTLIGHT.SLIDE_1_BODY'),
    route: accountScopedRoute('nervenet_supervisor'),
    screenshot: screenshot1,
    screenshotBg: '#E5E8FB',
  },
  {
    tag: t('HOME.SPOTLIGHT.TAG_NEW'),
    color: 'linear-gradient(135deg,#0e7f5b,#0a6448)',
    title: t('HOME.SPOTLIGHT.SLIDE_2_TITLE'),
    body: t('HOME.SPOTLIGHT.SLIDE_2_BODY'),
    route: accountScopedRoute('nervenet_commerce'),
    screenshot: screenshot2,
    screenshotBg: '#E3F2EC',
  },
  {
    tag: t('HOME.SPOTLIGHT.TAG_NEW'),
    color: 'linear-gradient(135deg,#b45309,#8f3f06)',
    title: t('HOME.SPOTLIGHT.SLIDE_3_TITLE'),
    body: t('HOME.SPOTLIGHT.SLIDE_3_BODY'),
    route: accountScopedRoute('nervenet_stay'),
    screenshot: screenshot3,
    screenshotBg: '#FBEFE0',
  },
]);

const spot = computed(() => slides.value[current.value]);

const startTimer = () => {
  timer = setInterval(() => {
    current.value = (current.value + 1) % slides.value.length;
  }, 6000);
};

const goTo = n => {
  clearInterval(timer);
  current.value =
    ((n % slides.value.length) + slides.value.length) % slides.value.length;
  startTimer();
};

onMounted(startTimer);
onUnmounted(() => clearInterval(timer));
</script>

<template>
  <div
    class="rounded-[18px] overflow-hidden border border-n-weak bg-gradient-to-br from-[#fbfbfd] to-[#f6f4ff] shadow-sm flex flex-col sm:flex-row"
  >
    <!-- Content panel -->
    <div
      class="flex-1 pt-6 px-[22px] pb-[26px] sm:p-11 flex flex-col sm:min-h-[240px]"
    >
      <div class="flex items-center gap-2.5 mb-5">
        <span
          class="text-[11px] font-bold tracking-[.08em] uppercase text-white rounded-full px-[11px] py-1"
          :style="{ background: spot.color }"
        >
          {{ spot.tag }}
        </span>
        <span
          v-if="spot.version"
          class="text-[13px] text-n-slate-9 font-semibold"
        >
          {{ spot.version }}
        </span>
      </div>

      <h2
        class="text-[23px] sm:text-[32px] font-extrabold tracking-tight leading-[1.1] mt-0 mb-[13px] max-w-[400px] text-n-slate-12"
      >
        {{ spot.title }}
      </h2>

      <p
        class="text-[15px] leading-relaxed text-n-slate-10 mt-0 mb-0 max-w-[420px]"
      >
        {{ spot.body }}
      </p>

      <div class="mt-auto pt-[30px] flex gap-2.5">
        <RouterLink
          :to="spot.route"
          class="no-underline text-white text-sm font-semibold rounded-[10px] px-[22px] py-3 transition-opacity hover:opacity-90"
          :style="{ background: spot.color }"
        >
          {{ t('HOME.SPOTLIGHT.CTA') }}
        </RouterLink>
      </div>

      <div class="flex items-center justify-between mt-[34px]">
        <div class="flex gap-[7px]">
          <button
            v-for="(_, idx) in slides"
            :key="idx"
            class="border-none rounded-full p-0 h-[7px] transition-all duration-[250ms] cursor-pointer"
            :style="{
              width: idx === current ? '24px' : '7px',
              background: idx === current ? '#16161a' : '#d2d2da',
            }"
            @click="goTo(idx)"
          />
        </div>
        <div class="flex gap-2">
          <button
            class="border border-n-weak bg-white text-n-slate-9 w-[34px] h-[34px] rounded-[9px] flex items-center justify-center cursor-pointer hover:border-n-slate-7 hover:text-n-slate-12 transition-colors"
            :title="t('HOME.SPOTLIGHT.PREV')"
            @click="goTo(current - 1)"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
          <button
            class="border border-n-weak bg-white text-n-slate-9 w-[34px] h-[34px] rounded-[9px] flex items-center justify-center cursor-pointer hover:border-n-slate-7 hover:text-n-slate-12 transition-colors"
            :title="t('HOME.SPOTLIGHT.NEXT')"
            @click="goTo(current + 1)"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Screenshot panel: banner on top (mobile), sidebar on right (sm+) -->
    <div
      class="order-first sm:order-none flex-none w-full sm:w-[44%] h-40 sm:h-auto border-b sm:border-b-0 sm:border-l border-n-weak overflow-hidden"
      :class="
        spot.screenshot
          ? 'flex items-center justify-center'
          : '[background-image:repeating-linear-gradient(135deg,#ececf3_0_1px,transparent_1px_11px)] flex items-center justify-center'
      "
      :style="
        spot.screenshot ? { background: spot.screenshotBg || '#EBE9F8' } : {}
      "
    >
      <img
        v-if="spot.screenshot"
        :src="spot.screenshot"
        class="w-full h-full object-contain"
        alt=""
      />
      <div
        v-else
        class="font-mono text-xs text-n-slate-8 bg-white/80 border border-dashed border-n-slate-6 rounded-lg px-3.5 py-2"
      />
    </div>
  </div>
</template>
